import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Recycle,
  Wallet,
  Settings,
  LogOut,
  Menu,
  ChevronRight,
  ChevronLeft,
  User,
  FileText,
  Building2,
  Apple,
  DollarSign,
  Package,
  BarChart3,
  UsersRound,
  Store,
  BookOpen,
  ListOrdered,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSettings } from '@/contexts/SettingsContext';

interface LayoutProps {
  user: any;
  onLogout: () => void;
}

type MenuItem = { id: string; label: string; icon: any; path: string };
type MenuGroup = { title: string; items: MenuItem[] };

// 员工菜单分组（简化版）
const employeeMenuGroups: MenuGroup[] = [
  {
    title: '我的工作',
    items: [
      { id: 'my-collections', label: '我的回收数据', icon: Recycle, path: '/' },
      { id: 'salaries', label: '我的工资', icon: Wallet, path: '/salaries' },
    ],
  },
];

// 司机菜单分组（司机端为全屏 H5 布局，此分组仅作兜底）
const driverMenuGroups: MenuGroup[] = [
  {
    title: '数据录入',
    items: [
      { id: 'store-collections', label: '门店回收录入', icon: Building2, path: '/' },
      { id: 'collections', label: '项目回收录入', icon: Recycle, path: '/collections' },
      { id: 'expenses', label: '费用报销', icon: FileText, path: '/expenses' },
    ],
  },
  {
    title: '个人中心',
    items: [{ id: 'salaries', label: '我的工资', icon: Wallet, path: '/salaries' }],
  },
];

const qixianViewerMenuGroups: MenuGroup[] = [
  {
    title: '项目查看',
    items: [{ id: 'store-collections', label: '京东七鲜项目', icon: Building2, path: '/store-collections' }],
  },
];

// 管理端菜单：三组平铺、无二级菜单（与视觉稿一致）
const adminMenuGroups: MenuGroup[] = [
  {
    title: '业务运营',
    items: [
      { id: 'dashboard', label: '仪表盘', icon: LayoutDashboard, path: '/' },
      { id: 'guoduomei-project', label: '果多美项目', icon: Apple, path: '/guoduomei-project' },
      { id: 'store-collections', label: '京东七鲜项目', icon: Building2, path: '/store-collections' },
      { id: 'store-income', label: '门店收入', icon: Wallet, path: '/store-income' },
      { id: 'store-assets', label: '门店资产', icon: Package, path: '/store-assets' },
      { id: 'projects', label: '项目记账', icon: BookOpen, path: '/projects' },
    ],
  },
  {
    title: '财务中心',
    items: [
      { id: 'finance', label: '财务总览', icon: BarChart3, path: '/finance' },
      { id: 'transactions', label: '交易明细', icon: ListOrdered, path: '/transactions' },
      { id: 'expenses', label: '费用报销', icon: FileText, path: '/expenses' },
      { id: 'other-expenses', label: '其它支出', icon: DollarSign, path: '/other-expenses' },
      { id: 'salaries', label: '工资管理', icon: Wallet, path: '/salaries' },
    ],
  },
  {
    title: '系统配置',
    items: [
      { id: 'store-assignments', label: '门店与分配', icon: Store, path: '/store-assignments' },
      { id: 'users', label: '用户管理', icon: User, path: '/users' },
      { id: 'settings', label: '系统设置', icon: Settings, path: '/settings' },
    ],
  },
];

// 顶栏「快捷新建」入口
const quickActions: MenuItem[] = [
  { id: 'q-store', label: '门店回收录入', icon: Building2, path: '/store-collections' },
  { id: 'q-project', label: '项目回收录入', icon: Recycle, path: '/collections' },
  { id: 'q-expense', label: '费用报销', icon: FileText, path: '/expenses' },
  { id: 'q-other', label: '其它支出', icon: DollarSign, path: '/other-expenses' },
];

export default function Layout({ user, onLogout }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const quickRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();

  // 司机端：全屏 H5 布局（无侧边栏无顶栏，页面自带绿色头部与底部四栏 Tab）
  const isDriver = user?.role === 'driver' || user?.role === 'employee';
  if (isDriver) {
    return (
      <div className="min-h-screen bg-[#f6faf7]">
        <main className="max-w-2xl mx-auto">
          <Outlet />
        </main>
      </div>
    );
  }

  const isEmployee = user?.role === 'staff';
  const isQixianViewer = user?.role === 'qixian_viewer';
  const menuGroups: MenuGroup[] = isQixianViewer ? qixianViewerMenuGroups : isEmployee ? employeeMenuGroups : adminMenuGroups;

  // 根据屏幕宽度自动调整侧边栏
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) setSidebarOpen(true);
      else if (window.innerWidth < 1024) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 点击外部关闭下拉
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (quickRef.current && !quickRef.current.contains(e.target as Node)) setQuickOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setQuickOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  const allItems = menuGroups.flatMap((g) => g.items);
  const currentItem = allItems.find(
    (item) => location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
  );
  const currentLabel = currentItem?.label || '仪表盘';

  const roleLabel =
    user?.role === 'admin' ? '超级管理员' : user?.role === 'manager' ? '经理' : isQixianViewer ? '七鲜查看员' : isEmployee ? '员工' : '操作员';
  const displayName = user?.real_name || user?.username || '管理员';
  const avatarChar = String(displayName).slice(0, 1);

  const expanded = sidebarOpen || mobileMenuOpen;

  const renderNavItem = (item: MenuItem, compact = false) => {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={() => mobileMenuOpen && setMobileMenuOpen(false)}
        title={compact ? item.label : undefined}
        className={cn(
          'group relative flex items-center rounded-lg transition-colors duration-150',
          compact ? 'w-10 h-10 justify-center' : 'gap-2.5 px-2.5 py-2',
          isActive ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        )}
      >
        <span
          className={cn(
            'flex-none flex items-center justify-center rounded-md',
            compact ? 'w-7 h-7' : 'w-6 h-6',
            isActive ? 'bg-emerald-100' : 'bg-slate-100 group-hover:bg-slate-200'
          )}
        >
          {Icon ? (
            <Icon className={cn('w-3.5 h-3.5', isActive ? 'text-emerald-700' : 'text-slate-500 group-hover:text-slate-700')} />
          ) : (
            <span className="text-[10px] text-slate-500">{item.label.slice(0, 1)}</span>
          )}
        </span>
        {!compact && <span className="text-[13px] whitespace-nowrap flex-1 truncate">{item.label}</span>}
        {compact && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            {item.label}
          </div>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* 移动端遮罩 */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* ===== 侧边栏 240px ===== */}
      <aside
        className={cn(
          'bg-white flex flex-col border-r border-slate-200 z-50 overflow-hidden transition-all duration-300',
          'fixed top-0 left-0 h-full shadow-xl lg:static lg:shadow-none lg:translate-x-0',
          mobileMenuOpen ? 'w-60 translate-x-0' : 'w-0 -translate-x-full lg:w-60',
          !expanded && 'lg:w-[68px]'
        )}
      >
        {/* 品牌区 56px */}
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-100 flex-none">
          <div className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white flex-none">
            <Recycle className="w-4 h-4" />
          </div>
          {expanded && (
            <div className="min-w-0 flex-1">
              <div
                className="font-semibold text-[13.5px] text-slate-900 leading-tight truncate"
                title={settings.site_name || '再生资源回收管理系统'}
              >
                {settings.site_name || '再生资源回收管理系统'}
              </div>
              <div
                className="text-[10.5px] text-slate-400 leading-tight truncate"
                title={settings.company_name || '再生资源回收管理系统'}
              >
                {settings.company_name || '再生资源回收管理系统'}
              </div>
            </div>
          )}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-1.5 rounded-md text-slate-400 hover:bg-slate-100"
            title="关闭菜单"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            title={sidebarOpen ? '收起菜单' : '展开菜单'}
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* 导航菜单（三组平铺，无二级） */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4 scrollbar-thin scrollbar-thumb-slate-200">
          {menuGroups.map((group) => (
            <div key={group.title}>
              {expanded && (
                <div className="px-2.5 mb-1.5 text-[10px] font-semibold text-slate-400 tracking-wide">
                  {group.title}
                </div>
              )}
              <div className={cn('space-y-0.5', !expanded && 'flex flex-col items-center')}>
                {group.items.map((item) => renderNavItem(item, !expanded))}
              </div>
            </div>
          ))}
        </nav>

        {/* 侧边栏底部用户区 */}
        <div className="border-t border-slate-100 p-2.5 flex-none">
          {expanded ? (
            <div className="flex items-center gap-2.5 px-1 py-1">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white text-[12px] font-semibold flex items-center justify-center flex-none">
                {avatarChar}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-slate-800 truncate">{displayName}</div>
                <div className="text-[10.5px] text-slate-400 truncate">{roleLabel}</div>
              </div>
              <button
                onClick={onLogout}
                className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                title="退出登录"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="hidden lg:flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white text-[12px] font-semibold flex items-center justify-center">
                {avatarChar}
              </div>
              <button
                onClick={onLogout}
                className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                title="退出登录"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ===== 主区 ===== */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 顶栏 56px */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-5 gap-3.5 flex-none">
          {/* 面包屑 */}
          <div className="flex items-center gap-2 text-[13px] min-w-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-1.5 -ml-1 rounded-md text-slate-600 hover:bg-slate-100"
              title="菜单"
            >
              <Menu className="w-4 h-4" />
            </button>
            <span className="text-slate-400 hidden sm:inline">首页</span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 hidden sm:inline" />
            <span className="font-semibold text-slate-900 truncate">{currentLabel}</span>
          </div>

          <div className="flex items-center gap-2.5">
            {/* 快捷新建 */}
            <div className="relative" ref={quickRef}>
              <button
                onClick={() => setQuickOpen(!quickOpen)}
                className="hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-600 text-white text-[12.5px] font-semibold hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                快捷新建
              </button>
              {quickOpen && (
                <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50">
                  {quickActions.map((a) => {
                    const Icon = a.icon;
                    return (
                      <button
                        key={a.id}
                        onClick={() => {
                          setQuickOpen(false);
                          navigate(a.path);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      >
                        <Icon className="w-3.5 h-3.5 text-slate-400" />
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <span className="hidden md:inline-block text-[12px] text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full whitespace-nowrap">
              {new Date().toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long',
              })}
            </span>
            <span className="hidden lg:inline-block text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
              {roleLabel}
            </span>

            {/* 用户菜单 */}
            <div className="relative" ref={userRef}>
              <button
                onClick={() => setUserOpen(!userOpen)}
                className="w-7 h-7 rounded-full bg-emerald-600 text-white text-[11px] font-semibold flex items-center justify-center"
              >
                {avatarChar}
              </button>
              {userOpen && (
                <div className="absolute right-0 mt-1.5 w-44 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-[13px] font-semibold text-slate-800 truncate">{displayName}</div>
                    <div className="text-[11px] text-slate-400">{roleLabel}</div>
                  </div>
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-rose-600 hover:bg-rose-50"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    退出登录
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* 页面内容：统一最大宽度 1440 + 24px 内边距 */}
        <main className="flex-1 overflow-auto">
          <div className="w-full max-w-[1440px] mx-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
